import {
  BackBar,
  CHANNEL_LABELS,
  COMMITTEE_ROLE_LABELS,
  fullDate,
  IdTag,
  isReachable,
  METER_STATUS_LABELS,
  PHONE_STATUS_LABELS,
} from '@horof/shared';
import { Alert, Button, Result, Skeleton, Tag } from 'antd';
import { Link, useParams } from 'react-router';
import { useQuery } from 'urql';
import { MemberDocuments } from './MemberDocuments';
import { MemberDetailsDocument } from './members-data';
import { ShartPeople } from './ShartPeople';

/**
 * A member, to look up and to send documents. Editing is done on the desk
 * (docs/changes/41–42); a new meter installed on site is added from here.
 */
export function MemberPage() {
  const { memberId = '' } = useParams();
  const [{ data, error }] = useQuery({
    query: MemberDetailsDocument,
    variables: { id: memberId },
  });

  if (!data) {
    return (
      <div className="page">
        <BackBar to="/members" label="الأعضاء" />
        {error ? (
          <Alert type="error" showIcon message="تعذّر تحميل العضو" />
        ) : (
          <Skeleton active paragraph={{ rows: 8 }} />
        )}
      </div>
    );
  }
  const member = data.member;
  if (!member) {
    return (
      <div className="page">
        <BackBar to="/members" label="الأعضاء" />
        <Result status="404" title="العضو غير موجود" />
      </div>
    );
  }
  const meters = member.meters.nodes.flatMap((m) => (m ? [m] : []));

  return (
    <div className="page">
      <BackBar to="/members" label="الأعضاء" />
      <section className="card">
        <h1 className="member-title">{member.fullName}</h1>
        <div className="field-hint">{member.memberAddress}</div>
        <dl className="details">
          <dt>الهاتف</dt>
          <dd>
            {member.notifyPhone ? (
              <a href={`tel:${member.notifyPhone}`} className="ltr-num">
                {member.notifyPhone}
              </a>
            ) : (
              '—'
            )}
            {member.contactPhone && member.contactName && (
              <div className="field-hint">({member.contactName})</div>
            )}
          </dd>
          <dt>التواصل</dt>
          <dd>
            <Tag color={isReachable(member) ? 'green' : 'orange'}>
              {member.notifyChannel === 'NONE'
                ? CHANNEL_LABELS.NONE
                : `${CHANNEL_LABELS[member.notifyChannel]} · ${PHONE_STATUS_LABELS[member.phoneStatus ?? 'MISSING']}`}
            </Tag>
          </dd>
          {member.committeeRole && (
            <>
              <dt>اللجنة</dt>
              <dd>
                <Tag color="purple">
                  {COMMITTEE_ROLE_LABELS[member.committeeRole]}
                </Tag>
              </dd>
            </>
          )}
          <dt>منذ</dt>
          <dd>{fullDate(member.joinedAt)}</dd>
        </dl>
        <div className="field-hint">
          تعديل المعلومات والشرط من الحاسوب («مكتب الجمعية»).
        </div>
      </section>

      <MemberDocuments
        member={{ ...member, unpaidCount: member.unpaid.totalCount }}
        associationName={data.currentSettings?.associationName ?? 'الجمعية'}
      />
      <ShartPeople
        people={member.shartPeople.nodes.flatMap((p) => (p ? [p] : []))}
      />

      <section className="card">
        <div className="field-label">العدادات ({meters.length})</div>
        {meters.length === 0 && (
          <div className="field-hint">لا عداد لهذا العضو.</div>
        )}
        <ul className="plain-list">
          {meters.map((m) => (
            <li key={m.id}>
              <Link to={`/meters/${m.id}`} className="meter-line">
                <span>
                  <strong>{m.label}</strong>
                  {m.meterAddress !== m.label && (
                    <div className="field-hint">{m.meterAddress}</div>
                  )}
                </span>
                <Tag
                  color={m.meterStatus === 'CONNECTED' ? 'green' : 'default'}
                >
                  {METER_STATUS_LABELS[m.meterStatus]}
                </Tag>
              </Link>
            </li>
          ))}
        </ul>
        <Link to={`/meters/new?member=${member.id}`}>
          <Button size="large" block>
            إضافة عداد (مع صورته)
          </Button>
        </Link>
      </section>
      <IdTag id={member.id} label="معرّف العضو" />
    </div>
  );
}
